import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, TrendingUp, Globe2, ArrowRight } from 'lucide-react';
import Reveal from '@/components/Reveal';

const PILLARS = [
  {
    icon: ShieldCheck,
    title: 'Trust Before Reach',
    body:
      'Influence without credibility is noise. Every representation, every campaign and every reported figure is verified before it reaches a client, because a brand’s reputation is borrowed the moment it is placed in our hands.',
  },
  {
    icon: TrendingUp,
    title: 'Influence as Infrastructure',
    body:
      'We treat cultural attention as a measurable commercial asset. Audience behaviour, conversion patterns and creative performance are modelled continuously, so growth is engineered rather than hoped for.',
  },
  {
    icon: Globe2,
    title: 'Power Across Borders',
    body:
      'From London and Cape Town we operate across nine markets and multiple time zones, giving our roster and our partners genuine global reach with local fluency behind it.',
  },
];

export default function MissionSection() {
  return (
    <section id="mission" className="relative bg-[#0B0B0B] py-24 lg:py-32 overflow-hidden border-t border-white/10">
      <div className="absolute inset-0 fine-grid opacity-50" />
      <div className="absolute bottom-0 left-0 w-2/3 h-full bg-[radial-gradient(circle_at_bottom_left,rgba(37,99,235,0.14),transparent_58%)]" />

      <div className="relative max-w-[1320px] mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#3B82F6] mb-5 block">
              Our Mission
            </span>
            <h2
              className="font-display font-bold text-white tracking-tight leading-[1.08]"
              style={{ fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
            >
              Building trust. Compounding power. Through digital influence.
            </h2>
            <p className="mt-7 text-white/60 text-base lg:text-lg font-light leading-relaxed">
              Ahana Digital Agency exists to convert cultural attention into durable commercial advantage.
              We represent a small, deliberately curated roster and produce the creative work that
              carries it — so that what an audience feels and what a balance sheet records are the
              same story.
            </p>
            <p className="mt-5 text-white/45 font-light leading-relaxed">
              Our standard is institutional: contracted terms, verified reporting, and an
              unwavering discipline about which brands sit beside which name. Influence, handled
              properly, is not exposure. It is leverage.
            </p>

            <Link
              to="/#contact"
              className="group mt-10 inline-flex items-center gap-2 text-sm font-semibold text-white hover:text-[#3B82F6] transition-colors"
            >
              Speak with our partnerships team
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>

          <div className="lg:col-span-7 flex flex-col gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={0.1 + i * 0.1}>
                <div className="group flex flex-col sm:flex-row gap-6 rounded-lg border border-white/10 bg-white/[0.03] p-7 lg:p-9 hover:border-[#3B82F6]/60 transition-all duration-500">
                  <div className="w-12 h-12 rounded-lg bg-[#2563EB]/12 border border-[#2563EB]/30 flex items-center justify-center shrink-0 group-hover:bg-[#2563EB] transition-colors duration-500">
                    <p.icon className="w-5 h-5 text-[#3B82F6] group-hover:text-white transition-colors duration-500" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-xl text-white tracking-tight">{p.title}</h3>
                    <p className="mt-3 text-white/55 font-light leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}